import { useMemo } from 'react';
import { create } from 'zustand';

import { useGroupsStore } from '@/features/groups/store';
import {
  createExpense,
  deleteExpense,
  listExpenses,
  listParticipantsForGroup,
  updateExpense,
  type ExpenseFilters,
  type ExpenseInput,
} from '@/features/expenses/repository';
import {
  createMember,
  deleteMember,
  listMembers,
  updateMember,
  type MemberInput,
} from '@/features/members/repository';
import {
  createSettlement,
  deleteSettlement,
  listSettlements,
  type SettlementInput,
} from '@/features/settlements/repository';
import { computeBalances, suggestSettlements } from '@domain/balance';
import type {
  ExpenseParticipant,
  ExpenseWithDetails,
  Member,
  MemberBalance,
  Settlement,
  SettlementSuggestion,
} from '@domain/types';

export const EMPTY_FILTERS: ExpenseFilters = {};

export function hasActiveFilters(filters: ExpenseFilters): boolean {
  return Boolean(
    filters.search?.trim() ||
      filters.categoryIds?.length ||
      filters.memberIds?.length ||
      filters.splitTypes?.length ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.uncategorisedOnly
  );
}

/**
 * Everything scoped to the open group, in one store.
 *
 * Members, expenses and settlements all feed the same balance calculation, so
 * they load and invalidate together — split across stores it becomes possible
 * to render a balance from a half-updated snapshot.
 */
type LedgerState = {
  groupId: string | null;
  members: Member[];
  /** Unfiltered — what balances and reports are computed from. */
  allExpenses: ExpenseWithDetails[];
  /** The filtered view rendered by the expenses table. */
  expenses: ExpenseWithDetails[];
  participants: ExpenseParticipant[];
  settlements: Settlement[];
  filters: ExpenseFilters;
  loading: boolean;
  error: string | null;

  load: (groupId: string) => Promise<void>;
  refresh: () => Promise<void>;
  setFilters: (filters: ExpenseFilters) => Promise<void>;
  resetFilters: () => void;

  addMember: (input: MemberInput) => Promise<void>;
  editMember: (id: string, patch: Partial<MemberInput & { isActive: boolean }>) => Promise<void>;
  removeMember: (id: string) => Promise<void>;

  addExpense: (input: Omit<ExpenseInput, 'groupId'>) => Promise<string>;
  editExpense: (id: string, input: Omit<ExpenseInput, 'groupId'>) => Promise<void>;
  removeExpense: (id: string) => Promise<void>;

  addSettlement: (input: Omit<SettlementInput, 'groupId'>) => Promise<void>;
  removeSettlement: (id: string) => Promise<void>;
};

async function fetchAll(groupId: string, filters: ExpenseFilters) {
  const [members, allExpenses, participants, settlements] = await Promise.all([
    listMembers(groupId),
    listExpenses(groupId),
    listParticipantsForGroup(groupId),
    listSettlements(groupId),
  ]);

  const expenses = hasActiveFilters(filters)
    ? await listExpenses(groupId, filters)
    : allExpenses;

  return { members, allExpenses, expenses, participants, settlements };
}

export const useLedgerStore = create<LedgerState>((set, get) => {
  /** Re-reads the whole group after a write. */
  async function reload(): Promise<void> {
    const { groupId, filters } = get();
    if (!groupId) return;
    set({ ...(await fetchAll(groupId, filters)), error: null });

    // The group list carries member/expense counts of its own; without this
    // the sidebar switcher keeps showing the counts from the last full load.
    await useGroupsStore.getState().refreshSummaries();
  }

  function requireGroupId(): string {
    const { groupId } = get();
    if (!groupId) throw new Error('No group is selected.');
    return groupId;
  }

  return {
    groupId: null,
    members: [],
    allExpenses: [],
    expenses: [],
    participants: [],
    settlements: [],
    filters: EMPTY_FILTERS,
    loading: false,
    error: null,

    load: async (groupId) => {
      const switchingGroup = get().groupId !== groupId;
      const filters = switchingGroup ? EMPTY_FILTERS : get().filters;

      set({ groupId, filters, loading: true, error: null });
      try {
        set({ ...(await fetchAll(groupId, filters)), loading: false });
      } catch (error) {
        set({ loading: false, error: (error as Error).message });
      }
    },

    refresh: async () => {
      try {
        await reload();
      } catch (error) {
        set({ error: (error as Error).message });
      }
    },

    setFilters: async (filters) => {
      const groupId = get().groupId;
      set({ filters });
      if (!groupId) return;

      try {
        const expenses = hasActiveFilters(filters)
          ? await listExpenses(groupId, filters)
          : get().allExpenses;
        set({ expenses });
      } catch (error) {
        set({ error: (error as Error).message });
      }
    },

    resetFilters: () => set({ filters: EMPTY_FILTERS, expenses: get().allExpenses }),

    addMember: async (input) => {
      await createMember(requireGroupId(), input);
      await reload();
    },

    editMember: async (id, patch) => {
      await updateMember(id, patch);
      await reload();
    },

    removeMember: async (id) => {
      await deleteMember(id);
      await reload();
    },

    addExpense: async (input) => {
      const id = await createExpense({ ...input, groupId: requireGroupId() });
      await reload();
      return id;
    },

    editExpense: async (id, input) => {
      await updateExpense(id, { ...input, groupId: requireGroupId() });
      await reload();
    },

    removeExpense: async (id) => {
      await deleteExpense(id);
      await reload();
    },

    addSettlement: async (input) => {
      await createSettlement({ ...input, groupId: requireGroupId() });
      await reload();
    },

    removeSettlement: async (id) => {
      await deleteSettlement(id);
      await reload();
    },
  };
});

export function useBalances(): MemberBalance[] {
  const members = useLedgerStore((state) => state.members);
  const expenses = useLedgerStore((state) => state.allExpenses);
  const participants = useLedgerStore((state) => state.participants);
  const settlements = useLedgerStore((state) => state.settlements);

  return useMemo(
    () => computeBalances({ members, expenses, participants, settlements }),
    [members, expenses, participants, settlements]
  );
}

export function useSettlementPlan(): SettlementSuggestion[] {
  const balances = useBalances();
  const expenses = useLedgerStore((state) => state.allExpenses);
  const participants = useLedgerStore((state) => state.participants);
  const settlements = useLedgerStore((state) => state.settlements);

  return useMemo(
    () => suggestSettlements(balances, expenses, participants, settlements),
    [balances, expenses, participants, settlements]
  );
}

export function useMemberLookup(): Map<string, Member> {
  const members = useLedgerStore((state) => state.members);
  return useMemo(() => new Map(members.map((member) => [member.id, member])), [members]);
}
